import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { FaBolt, FaCaretDown, FaCaretUp, FaCog, FaEnvelope, FaExclamationTriangle, FaFire, FaInfoCircle, FaSeedling, FaSnowflake, FaSpinner, FaTimes, FaTrashAlt } from "react-icons/fa";
import { calcScore, hasUsableWebsite, scoreLabel } from "../appShared";
import InfoTab from "./InfoTab";
import QualifyTab from "./QualifyTab";

function scoreBreakdown(lead) {
  const items = [];
  const employees = lead.employees || 0;
  const usableWebsite = hasUsableWebsite(lead);
  const websiteStatus = usableWebsite ? (lead?.websiteStatus || "found") : (lead?.websiteStatus || "unknown");

  if (websiteStatus === "missing") {
    items.push({ pts: 35, label: "No website - high opportunity", flag: "opportunity" });
  } else if (websiteStatus === "unknown") {
    items.push({ pts: 5, label: "Website status unknown - not yet verified", flag: "mid" });
  } else {
    items.push({ pts: 5, label: "Has website - lower demand", flag: "low" });
  }

  if (employees >= 1 && employees <= 5) items.push({ pts: 30, label: "Tiny team (1-5) - easy to reach", flag: "high" });
  else if (employees >= 6 && employees <= 20) items.push({ pts: 25, label: "Small team (6-20) - good target", flag: "high" });
  else if (employees >= 21 && employees <= 50) items.push({ pts: 15, label: "Mid-size team (21-50)", flag: "mid" });
  else if (employees >= 51 && employees <= 200) items.push({ pts: 10, label: "Large team (51-200) - harder to close", flag: "low" });
  else items.push({ pts: 20, label: "Team size unknown - assumed small", flag: "mid" });

  const industry = (lead.industry || "").toLowerCase();
  const highFit = ["retail", "restaurant", "food", "construction", "design", "marketing", "fashion", "beauty", "gym", "fitness", "salon", "real estate", "hotel", "hospitality", "event", "craft", "trade", "plumbing", "electrical", "tannlege", "frisor", "rorlegger", "maler", "snekker", "bakeri", "blomster", "kafe"];
  const goodFit = ["it", "software", "consulting", "education", "health", "legal", "finance", "accounting", "recruitment", "logistics", "transport", "nyhets", "media", "sport"];
  if (highFit.some((keyword) => industry.includes(keyword))) items.push({ pts: 25, label: `"${lead.industry}" - high industry fit`, flag: "high" });
  else if (goodFit.some((keyword) => industry.includes(keyword))) items.push({ pts: 10, label: `"${lead.industry}" - medium industry fit`, flag: "mid" });
  else items.push({ pts: 15, label: `"${lead.industry || "Unknown industry"}" - unverified fit`, flag: "mid" });

  if (lead.phone) items.push({ pts: 5, label: "Has phone number", flag: "high" });
  if (lead.email) items.push({ pts: 5, label: "Has email address", flag: "high" });

  if (websiteStatus === "missing") items.push({ pts: 20, label: "No confirmed website - priority boost", flag: "opportunity" });
  else if (websiteStatus === "unknown") items.push({ pts: 10, label: "Unconfirmed website - priority boost", flag: "mid" });

  return items;
}
export default function DetailPanel({ lead, detailTab, setDetailTab, updateLead, deleteLead, findEmail, crawlEmail, crawlProjectDetails, qualifyWithAI, aiQualifying, generateOutreach, outreachLoading, outreachMsg, setOutreachMsg, onClose, setConfirmModal, gmailStatus, sendEmail, searching, OutreachTabComponent }) {
  const liveScore = calcScore(lead);
  const { label, emoji, color } = scoreLabel(liveScore);
  const breakdown = scoreBreakdown(lead);
  const [showBreakdown, setShowBreakdown] = React.useState(false);
  const [revalidating, setRevalidating] = React.useState(false);
  const [note, setNote] = useState(lead.note || "");

  useEffect(() => { setNote(lead.note || ""); }, [lead.id, lead.note]);

  useEffect(() => {
    const autoBlocked = /news\.google\.com|linkedin\.com|facebook\.com|instagram\.com|twitter\.com|x\.com|youtube\.com/i.test(lead.website || "");
    if (!lead.email && lead.website && !autoBlocked && lead.sourceType !== "Google News Public Feed") {
      findEmail(lead);
    }
  }, [lead, lead.email, lead.website, lead.sourceType, findEmail]);

  const revalidateScore = async () => {
    setRevalidating(true);
    const newScore = calcScore(lead);
    await updateLead(lead.id, { score: newScore });
    setTimeout(() => setRevalidating(false), 800);
    toast.success(`Score updated: ${newScore}/100`);
  };

  const saveNote = () => updateLead(lead.id, { note });

  return (
    <div className="detail-panel">
      <div className="detail-header">
        <button className="detail-score-badge" style={{ background: color + "18", color, border: "none", cursor: "pointer" }} onClick={() => setShowBreakdown((open) => !open)} title="Click to see score breakdown">
          {emoji === "fire" ? <FaFire /> : emoji === "bolt" ? <FaBolt /> : emoji === "seedling" ? <FaSeedling /> : <FaSnowflake />} {liveScore} {label} <span style={{ fontSize: "10px", opacity: 0.6 }}>{showBreakdown ? <FaCaretUp /> : <FaCaretDown />}</span>
        </button>
        <button className={`btn-revalidate ${revalidating ? "btn-revalidating" : ""}`} onClick={revalidateScore} disabled={revalidating} title="Re-calculate score based on current lead data">
          {revalidating ? <FaSpinner className="spin" /> : "↻"} Re-score
        </button>
        <button className="detail-close" onClick={onClose}><FaTimes /></button>
      </div>

      {showBreakdown && (
        <div className="score-breakdown-panel">
          <div className="sbp-title">Why {liveScore}/100?</div>
          {breakdown.map((item, index) => (
            <div key={index} className={`sbp-row sbp-${item.flag}`}>
              <span className="sbp-pts">+{item.pts}</span>
              <span className="sbp-label">{item.label}</span>
            </div>
          ))}
          {lead.websiteStatus === "missing" && (
            <div className="sbp-tip"><FaExclamationTriangle style={{ marginRight: 6 }} />Score will drop to ~{calcScore({ ...lead, website: "x" })} once you add their website</div>
          )}
        </div>
      )}

      <div className="detail-name">{lead.name}</div>
      <div className="detail-sub">{lead.industry || "—"} · {[lead.city, lead.country].filter(Boolean).join(", ")}</div>

      <div className="detail-status-bar">
        {["New", "Contacted", "Interested", "Won", "Lost"].map((status) => (
          <button key={status} className={`status-pill ${lead.status === status ? "active" : ""}`} onClick={() => updateLead(lead.id, { status })}>{status}</button>
        ))}
      </div>

      <div className="detail-tabs">
        {["info", "qualify", "outreach"].map((tab) => (
          <button key={tab} className={`detail-tab ${detailTab === tab ? "active" : ""}`} onClick={() => setDetailTab(tab)}>
            {tab === "info" ? <><FaInfoCircle style={{ marginRight: 6 }} />Info</> : tab === "qualify" ? <><FaCog style={{ marginRight: 6 }} />Qualify</> : <><FaEnvelope style={{ marginRight: 6 }} />Outreach</>}
          </button>
        ))}
      </div>

      <div className="detail-content">
        {detailTab === "info" && <InfoTab lead={lead} updateLead={updateLead} findEmail={findEmail} crawlEmail={crawlEmail} crawlProjectDetails={crawlProjectDetails} setNote={setNote} note={note} saveNote={saveNote} searching={searching} />}
        {detailTab === "qualify" && <QualifyTab lead={lead} updateLead={updateLead} qualifyWithAI={qualifyWithAI} aiQualifying={aiQualifying} setDetailTab={setDetailTab} searching={searching} />}
        {detailTab === "outreach" && OutreachTabComponent && <OutreachTabComponent lead={lead} updateLead={updateLead} crawlEmail={crawlEmail} crawlProjectDetails={crawlProjectDetails} generateOutreach={generateOutreach} outreachLoading={outreachLoading} outreachMsg={outreachMsg} setOutreachMsg={setOutreachMsg} gmailStatus={gmailStatus} sendEmail={sendEmail} findEmail={findEmail} searching={searching} />}
      </div>

      <div className="detail-footer">
        <button className="btn-danger-ghost btn-sm" onClick={() => setConfirmModal({ message: `Delete ${lead.name}?`, onConfirm: () => deleteLead(lead.id) })}>
          <FaTrashAlt style={{ marginRight: 6 }} />Delete
        </button>
        <span className="detail-added">Added {lead.addedAt || "today"}</span>
      </div>
    </div>
  );
}